import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaStar, FaShoppingCart, FaHeart, FaShare, FaArrowLeft, FaLeaf, FaFire, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import { addToCart } from './Cart';
import { API_ENDPOINTS } from '../config/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedPriceType, setSelectedPriceType] = useState('single');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const { t } = useTranslation();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.PRODUCTS.GET(id));
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const getCurrentPrice = () => {
    if (!product) return 0;
    switch (selectedPriceType) {
      case 'single': return product.price_single;
      case 'box': return product.price_box;
      case 'packet': return product.price_packet;
      default: return product.price_single;
    }
  };

  const getAvailablePriceTypes = useCallback(() => {
    if (!product) return [];
    const available = [];
    if (product.price_single && product.price_single > 0) available.push('single');
    if (product.price_box && product.price_box > 0) available.push('box');
    if (product.price_packet && product.price_packet > 0) available.push('packet');
    return available;
  }, [product]);

  useEffect(() => {
    if (product) {
      const available = getAvailablePriceTypes();
      if (available.length > 0 && !available.includes(selectedPriceType)) {
        setSelectedPriceType(available[0]);
      }
    }
  }, [product, selectedPriceType, getAvailablePriceTypes]);

  const handleAddToCart = () => {
    if (!product) return;
    const productWithPrice = {
      ...product,
      price: getCurrentPrice(),
      priceType: selectedPriceType
    };
    addToCart(productWithPrice, quantity);
    navigate('/cart');
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const showNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === (product.images?.length || 0) - 1 ? 0 : prevIndex + 1
    );
  };

  const showPrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? (product.images?.length || 0) - 1 : prevIndex - 1
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cigar-cream flex items-center justify-center">
        <div className="text-2xl text-cigar-dark">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-cigar-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-cigar font-bold text-cigar-dark mb-4">{t('productDetail.productNotFound')}</h1>
          <p className="text-gray-600 mb-6">{t('productDetail.productNotFoundDescription')}</p>
          <button
            onClick={() => navigate('/products')}
            className="btn-primary"
          >
            {t('productDetail.backToProducts')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cigar-cream py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/products')}
            className="flex items-center text-cigar-brown hover:text-cigar-dark transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            {t('productDetail.backToProducts')}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Product Images */}
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer" onClick={() => openModal(0)}>
              <img
                src={(product.images && product.images[0]) || product.image || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=900&fit=crop'}
                alt={product.product_name}
                className="w-full h-auto object-contain m-0 p-0 border-0 rounded-none"
              />
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                {product.images.map((img, index) => (
                  <div
                    key={index}
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg shadow-md overflow-hidden cursor-pointer border-2 border-transparent hover:border-cigar-gold transition-all duration-200"
                    onClick={() => openModal(index)}
                  >
                    <img
                      src={img}
                      alt={`${product.product_name} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-4 sm:space-y-6">
            {/* Brand and Category Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="bg-cigar-gold text-cigar-dark px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                {product.brand || t('productDetail.premiumBrand')}
              </span>
              <span className="bg-cigar-brown text-cigar-cream px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                {product.category || t('productDetail.premiumCategory')}
              </span>
            </div>

            {/* Product Name */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-cigar font-bold text-cigar-dark">
              {product.product_name}
            </h1>

            {/* Rating */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <FaStar
                    key={i}
                    className={`w-5 h-5 ${i < (product.rating || 4) ? 'text-cigar-gold' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <span className="text-gray-600">({product.rating || 4}.0)</span>
            </div>

            {/* Pricing Options */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-cigar-dark">{t('productDetail.pricingOptions')}</h3>
              
              {/* Price Type Selection */}
              <div className={`grid gap-2 ${getAvailablePriceTypes().length === 1 ? 'grid-cols-1' : getAvailablePriceTypes().length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                {product.price_single && product.price_single > 0 && (
                  <button
                    onClick={() => setSelectedPriceType('single')}
                    className={`p-2 sm:p-3 rounded-lg border-2 transition-all duration-200 ${
                      selectedPriceType === 'single'
                        ? 'border-cigar-gold bg-cigar-gold/10 text-cigar-dark'
                        : 'border-gray-300 hover:border-cigar-gold text-gray-700'
                    }`}
                  >
                    <div className="text-xs sm:text-sm font-medium">{t('productDetail.singlePrice')}</div>
                    <div className="text-sm sm:text-lg font-bold">{product.currency} {product.price_single}</div>
                  </button>
                )}
                
                {product.price_box && product.price_box > 0 && (
                  <button
                    onClick={() => setSelectedPriceType('box')}
                    className={`p-2 sm:p-3 rounded-lg border-2 transition-all duration-200 ${
                      selectedPriceType === 'box'
                        ? 'border-cigar-gold bg-cigar-gold/10 text-cigar-dark'
                        : 'border-gray-300 hover:border-cigar-gold text-gray-700'
                    }`}
                  >
                    <div className="text-xs sm:text-sm font-medium">{t('productDetail.boxPrice', { boxSize: product.box_size })}</div>
                    <div className="text-sm sm:text-lg font-bold">{product.currency} {product.price_box}</div>
                  </button>
                )}
                
                {product.price_packet && product.price_packet > 0 && (
                  <button
                    onClick={() => setSelectedPriceType('packet')}
                    className={`p-2 sm:p-3 rounded-lg border-2 transition-all duration-200 ${
                      selectedPriceType === 'packet'
                        ? 'border-cigar-gold bg-cigar-gold/10 text-cigar-dark'
                        : 'border-gray-300 hover:border-cigar-gold text-gray-700'
                    }`}
                  >
                    <div className="text-xs sm:text-sm font-medium">{t('productDetail.packetPrice')}</div>
                    <div className="text-sm sm:text-lg font-bold">{product.currency} {product.price_packet}</div>
                  </button>
                )}
              </div>
              
              {/* Selected Price Display */}
              <div className="bg-cigar-cream p-3 sm:p-4 rounded-lg">
                <div className="text-xs sm:text-sm text-gray-600">{t('productDetail.selectedPrice')}:</div>
                <div className="text-2xl sm:text-3xl font-bold text-cigar-dark">
                  {product.currency} {getCurrentPrice()}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {selectedPriceType === 'single' && t('productDetail.perSingleUnit')}
                  {selectedPriceType === 'box' && t('productDetail.perBoxUnits', { boxSize: product.box_size })}
                  {selectedPriceType === 'packet' && t('productDetail.perPacket')}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-base sm:text-lg font-semibold text-cigar-dark">{t('productDetail.description')}</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {product.description || t('productDetail.productDescription')}
              </p>
            </div>

            {/* Features */}
            <div className="space-y-2 sm:space-y-3">
              <h3 className="text-base sm:text-lg font-semibold text-cigar-dark">{t('productDetail.features')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="flex items-center space-x-2">
                  <FaLeaf className="text-cigar-gold w-4 h-4" />
                  <span className="text-sm sm:text-base text-gray-600">{t('productDetail.handcrafted')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaFire className="text-cigar-gold w-4 h-4" />
                  <span className="text-sm sm:text-base text-gray-600">{t('productDetail.premiumTobacco')}</span>
                </div>
              </div>
            </div>

            {/* Quantity and Add to Cart */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <label className="text-xs sm:text-sm font-medium text-gray-700">{t('productDetail.quantity')}:</label>
                <div className="flex items-center border border-gray-300 rounded-md">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 text-sm sm:text-base"
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="px-3 sm:px-4 py-2 border-x border-gray-300 min-w-[2.5rem] sm:min-w-[3rem] text-center text-sm sm:text-base">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 text-sm sm:text-base"
                    disabled={quantity >= 10}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-cigar-brown hover:bg-cigar-dark text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <FaShoppingCart />
                  <span>{t('productDetail.addToCart')}</span>
                </button>
                <div className="flex space-x-2 sm:space-x-4">
                  <button className="p-3 border border-cigar-brown text-cigar-brown hover:bg-cigar-brown hover:text-white rounded-lg transition-colors duration-200">
                    <FaHeart className="w-4 h-4" />
                  </button>
                  <button className="p-3 border border-cigar-brown text-cigar-brown hover:bg-cigar-brown hover:text-white rounded-lg transition-colors duration-200">
                    <FaShare className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="pt-4 sm:pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                <div>
                  <span className="font-medium">{t('productDetail.brand')}:</span> {product.brand || t('productDetail.premiumBrand')}
                </div>
                <div>
                  <span className="font-medium">{t('productDetail.category')}:</span> {product.category || t('productDetail.premiumCategory')}
                </div>
                <div>
                  <span className="font-medium">{t('productDetail.boxSize')}:</span> {product.box_size} {t('productDetail.units')}
                </div>
                <div>
                  <span className="font-medium">{t('productDetail.currency')}:</span> {product.currency}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && product.images && product.images.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-full overflow-hidden">
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 bg-cigar-dark text-white rounded-full p-2 hover:bg-cigar-brown transition-colors z-10"
            >
              <FaTimes className="w-5 h-5" />
            </button>

            <div className="flex items-center justify-center h-full p-4">
              <button
                onClick={showPrevImage}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-cigar-dark text-white rounded-full p-2 hover:bg-cigar-brown transition-colors z-10"
              >
                <FaChevronLeft className="w-5 h-5" />
              </button>
              <img
                src={product.images[currentImageIndex]}
                alt={`${product.product_name} - ${currentImageIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain"
              />
              <button
                onClick={showNextImage}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-cigar-dark text-white rounded-full p-2 hover:bg-cigar-brown transition-colors z-10"
              >
                <FaChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
